import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { useAlert } from '../../../contexts/AlertContext';
import EmployeeSearch from '../EmployeeSearch';

const ImpersonationDialog = ({ open, onClose }) => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { startImpersonation } = useAuth();
  const { showAlert } = useAlert();

  const handleStartImpersonation = async () => {
    if (!selectedEmployee) {
      setError('Please select an employee to impersonate');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await startImpersonation(selectedEmployee.id, 'Administrative access via UI');
      showAlert(`Now impersonating ${selectedEmployee.fullName}`, 'info');
      handleClose();
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      setError(error.message || 'Failed to start impersonation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedEmployee(null);
    setError('');
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Start User Impersonation
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Select a consultant to impersonate. You will be able to view the system as that user.
            This action is logged for security purposes.
          </Typography>

          <EmployeeSearch
            value={selectedEmployee}
            onChange={setSelectedEmployee}
            label="Select Individual to Impersonate"
            placeholder="Search individuals by name..."
            width={500}
            height="56px"
            showSelectedAvatar={true}
          />

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleStartImpersonation}
          variant="contained"
          disabled={loading || !selectedEmployee}
          startIcon={loading ? <CircularProgress size={18} /> : null}
        >
          {loading ? 'Starting...' : 'Start Impersonation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImpersonationDialog;